import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Copy,
  Download as DownloadIcon,
  FileVideo,
  FileAudio,
  FileImage,
  FileText,
  File,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { getShare, getShareFiles } from "@/services/share-service";
import LoadingSpinner from "@/components/LoadingSpinner";
import { toast } from "sonner";
import { makeFileUrl } from "@/services/file-service";

interface SharedFile {
  name: string;
  size: number;
  type: string;
  key: string;
}

const Download = () => {
  const { shareId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordRequired, setIsPasswordRequired] = useState(false);
  const [password, setPassword] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [files, setFiles] = useState<SharedFile[]>([]);

  useEffect(() => {
    const loadShare = async () => {
      try {
        const share = await getShare(shareId);
        if (share.hasPassword) {
          setIsPasswordRequired(true);
        } else if (share.files?.length) {
          setFiles(share.files);
        }
      } catch (error) {
        toast.error("Failed to load recent shares");
      } finally {
        setIsLoading(false);
      }
    };

    loadShare();
  }, [shareId]);

  const verifyPassword = async () => {
    try {
      const files = await getShareFiles(shareId, password);
      setFiles(files);
      setIsVerified(true);
    } catch (e) {
      toast.error("Invalid password. Please try again.");
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (type: string) => {
    const iconProps = { size: 20 };
    switch (type.toLowerCase()) {
      case "mp4":
      case "mov":
      case "avi":
        return <FileVideo {...iconProps} />;
      case "mp3":
      case "wav":
        return <FileAudio {...iconProps} />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <FileImage {...iconProps} />;
      case "pdf":
        return <FileText {...iconProps} />;
      default:
        return <File {...iconProps} />;
    }
  };

  const copyUrl = (key: string, name: string) => {
    const apiUrl = makeFileUrl(key, true, false, name);
    console.log(apiUrl);
    navigator.clipboard.writeText(apiUrl);
    toast.success("The file URL has been copied to your clipboard");
  };

  const copyAllUrls = () => {
    const urls = files
      .map(({ key, name }) => makeFileUrl(key, true, false, name))
      .join("\n");
    navigator.clipboard.writeText(urls);
    toast.success("All file URLs have been copied to your clipboard");
  };

  const downloadAll = () => {
    files.forEach(({ key, name }) => {
      window.open(makeFileUrl(key, true, false, name), "_blank");
    });
    toast.success("All files will begin downloading shortly");
  };

  if (!shareId) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Invalid Share Link
          </h1>
          <p className="mt-2 text-gray-600">
            This share link appears to be invalid or expired.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isPasswordRequired && !isVerified) {
    return (
      <Dialog open={true}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enter Password</DialogTitle>
            <DialogDescription>
              This shared content is password protected. Please enter the
              password to continue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && password.length) {
                  verifyPassword();
                }
              }}
            />
            <Button
              disabled={!password}
              onClick={verifyPassword}
              className="w-full"
            >
              Verify Password
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Shared Files</h1>
        <div className="space-x-2">
          <Button variant="outline" onClick={copyAllUrls}>
            <Copy size={16} className="mr-2" />
            Copy All URLs
          </Button>
          <Button onClick={downloadAll}>
            <DownloadIcon size={16} className="mr-2" />
            Download All
          </Button>
        </div>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.key}>
                <TableCell className="flex items-center space-x-2">
                  {getFileIcon(file.type)}
                  <span>{file.name}</span>
                </TableCell>
                <TableCell>{formatFileSize(file.size)}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyUrl(file.key, file.name)}
                    >
                      <Copy size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        window.open(
                          makeFileUrl(file.key, true, false, file.name),
                          "_blank"
                        )
                      }
                    >
                      <DownloadIcon size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Download;
